import React from 'react';
import { createRoot } from 'react-dom/client'; // Import createRoot from react-dom/client
import axios from 'axios';
import App from './App';
import './styles.css';
import { GoogleOAuthProvider } from '@react-oauth/google'; // Import GoogleOAuthProvider
import { createTheme, ThemeProvider } from '@mui/material/styles';
import config from './config';

import { SnackbarProvider } from './components/SnackbarContext';
import AppErrorBoundary from './components/common/AppErrorBoundary';

const theme = createTheme();

// Global request timeout. Without it, a request made while the device is on a
// dead-but-associated network hangs forever and the calling screen stays stuck
// on its loading spinner with no error path.
axios.defaults.timeout = 20000;

// --- Stale-chunk recovery -------------------------------------------------
// After a deploy, an already-open client still holds the old index.html and
// asks for code-split chunks that no longer exist on the CDN. React Router's
// lazy import then rejects with ChunkLoadError and the screen goes blank.
// Reloading once picks up the new index.html. The sessionStorage flag makes it
// strictly one-shot, so a genuinely broken build can't become a reload loop.
const CHUNK_RELOAD_FLAG = 'parianda:chunkReloadAttempted';

const isChunkLoadError = (value) => {
  if (!value) return false;
  const name = (value && value.name) || '';
  const message =
    (value && value.message) ||
    (typeof value === 'string' ? value : '') ||
    '';
  return (
    name === 'ChunkLoadError' ||
    message.indexOf('ChunkLoadError') !== -1 ||
    message.indexOf('Loading chunk') !== -1 ||
    message.indexOf('Loading CSS chunk') !== -1
  );
};

const reloadOnceForChunkError = () => {
  let alreadyTried = false;
  try {
    alreadyTried = window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
  } catch (e) {
    // Private mode / blocked storage: fall through and skip the reload rather
    // than risk an unguarded loop.
    alreadyTried = true;
  }
  if (alreadyTried) return;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  } catch (e) {
    return;
  }
  console.warn('[index] Stale chunk detected — reloading once to pick up the new build');
  window.location.reload();
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event && event.reason;
  if (isChunkLoadError(reason)) reloadOnceForChunkError();
});

window.addEventListener('error', (event) => {
  if (isChunkLoadError((event && event.error) || (event && event.message))) {
    reloadOnceForChunkError();
  }
});

// Get the root element
const container = document.getElementById('root');

// Create a root
const root = createRoot(container);

// Render the App component
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID_WEB}>
        <ThemeProvider theme={theme}>
         <SnackbarProvider>
           <App />
          </SnackbarProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
